import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { StateOptions, TRoles } from '../../../../shared/const'
import { stateLabel } from '../../../../shared/helpers'
import { buildAdminStats, RECENT_LOGIN_DAYS, WITHOUT_STATE } from './stats'

import styles from './overview.module.scss'

/*
 * Übersicht über der Administratoren-Liste.
 *
 * Beantwortet drei Fragen auf einen Blick, für die man sonst die Liste
 * durchzählen müsste: Wie viele Zugänge gibt es, wie verteilen sie sich auf
 * die beiden Rollen, und welches Bundesland hat wie viele Landesadmins —
 * inklusive der Bundesländer ohne einen einzigen.
 *
 * Gezeichnet wird mit Rechtecken aus CSS, nicht mit einer Diagramm-Bibliothek:
 * Für zwei Balkengruppen wäre ein weiteres Paket im Auslieferungspaket nicht
 * angebracht. Die Zahl steht immer als Text daneben, der Balken ist nur die
 * Anschauung dazu.
 *
 * Jeder Balken ist zugleich der Weg in die Liste darunter: Ein Klick auf
 * „Bundesadmin" oder „Stmk" zeigt nur noch diese Zugänge. Die Balken selbst
 * rechnen dabei weiter mit dem ganzen Bestand — sonst schrumpfte beim ersten
 * Klick alles auf die eigene Auswahl zusammen und man käme nicht mehr zu einer
 * anderen. Was gerade gilt, steht als Zeile unter den Balken, mit einem Knopf
 * zum Zurücksetzen.
 */

const roleLabels = {
	[TRoles.FEDERAL_ADMIN]: 'Federaladmin',
	[TRoles.ADMIN]: 'Stateadmin',
	[TRoles.ALUMNI]: 'ALUMNI'
}

const Bar = ({ label, count, max, tone, active, dimmed, onToggle, title }) => (
	<li className={styles.barRow}>
		<button
			type="button"
			className={clsx(styles.barBtn, active && styles.barBtnActive, dimmed && styles.barBtnDimmed)}
			// `aria-pressed` sagt der Vorlesehilfe, dass der Knopf einen Zustand
			// schaltet und nicht irgendwohin führt.
			aria-pressed={active}
			title={title}
			onClick={onToggle}
		>
			<span className={styles.barLabel}>{label}</span>
			<span className={styles.barTrack}>
				<span
					aria-hidden="true"
					className={tone === 'federal' ? styles.barFillFederal : styles.barFill}
					// Bei Null bleibt ein feiner Strich stehen, damit die Zeile
					// nicht wie ein Darstellungsfehler aussieht.
					style={{ width: max > 0 ? `${Math.max((count / max) * 100, count > 0 ? 4 : 0)}%` : 0 }}
				/>
			</span>
			<span className={styles.barValue}>{count}</span>
		</button>
	</li>
)

Bar.propTypes = {
	label: PropTypes.string.isRequired,
	count: PropTypes.number.isRequired,
	max: PropTypes.number.isRequired,
	tone: PropTypes.string,
	active: PropTypes.bool,
	dimmed: PropTypes.bool,
	onToggle: PropTypes.func.isRequired,
	title: PropTypes.string
}

export default function Overview(props) {
	const { t } = useTranslation()
	const { filter, onToggle, onReset, visibleCount } = props

	const stateOrder = useMemo(() => StateOptions.map(option => option.value), [])
	const stats = useMemo(() => buildAdminStats(props.admins, stateOrder), [props.admins, stateOrder])

	const maxRole = stats.byRole.reduce((acc, entry) => Math.max(acc, entry.count), 0)

	// Beschriftung der Merkzettel unter den Balken. Sie benennen genau das, was
	// im Balken angeklickt wurde — sonst weiß man nach dem Scrollen nicht mehr,
	// warum die Liste kurz ist.
	const chips = [
		filter.role && {
			kind: 'role',
			value: filter.role,
			label: t(roleLabels[filter.role] || filter.role)
		},
		filter.state && {
			kind: 'state',
			value: filter.state,
			label: filter.state === WITHOUT_STATE ? t('Without federal state') : stateLabel(filter.state)
		}
	].filter(Boolean)

	return (
		<section className={styles.root} aria-label={t('Administrators at a glance')}>
			<div className={styles.tiles}>
				<div className={styles.tile}>
					<span className={styles.tileValue}>{stats.total}</span>
					<span className={styles.tileLabel}>{t('Administrators')}</span>
				</div>
				{stats.byRole.map(entry => (
					<div className={styles.tile} key={entry.role}>
						<span className={styles.tileValue}>{entry.count}</span>
						<span className={styles.tileLabel}>{t(roleLabels[entry.role])}</span>
					</div>
				))}
				<div className={styles.tile}>
					<span className={styles.tileValue}>{stats.recentLogins}</span>
					<span className={styles.tileLabel}>
						{t('Signed in within {{days}} days', { days: RECENT_LOGIN_DAYS })}
					</span>
				</div>
			</div>

			<div className={styles.charts}>
				<div className={styles.chart}>
					<h3 className={styles.chartTitle}>{t('By role')}</h3>
					<ul className={styles.bars}>
						{stats.byRole.map(entry => (
							<Bar
								key={entry.role}
								label={t(roleLabels[entry.role])}
								count={entry.count}
								max={maxRole}
								tone={entry.role === TRoles.FEDERAL_ADMIN ? 'federal' : undefined}
								active={filter.role === entry.role}
								dimmed={Boolean(filter.role) && filter.role !== entry.role}
								title={t('Filter the list by {{label}}', { label: t(roleLabels[entry.role]) })}
								onToggle={() => onToggle('role', entry.role)}
							/>
						))}
					</ul>
				</div>

				<div className={styles.chart}>
					<h3 className={styles.chartTitle}>{t('By state')}</h3>
					<ul className={styles.bars}>
						{stats.byState.map(entry => (
							<Bar
								key={entry.state}
								label={stateLabel(entry.state)}
								count={entry.count}
								max={stats.maxState}
								active={filter.state === entry.state}
								dimmed={Boolean(filter.state) && filter.state !== entry.state}
								title={t('Filter the list by {{label}}', { label: stateLabel(entry.state) })}
								onToggle={() => onToggle('state', entry.state)}
							/>
						))}
						{/*
						 * Wer kein Bundesland hat, ist österreichweit zuständig
						 * (in aller Regel die Bundesadmins) und taucht in keinem
						 * Balken darüber auf. Ohne diese Zeile ergäbe die Summe
						 * der Balken weniger als die Gesamtzahl in der Kachel.
						 */}
						<Bar
							label={t('Without federal state')}
							count={stats.withoutState}
							max={stats.maxState}
							tone="federal"
							active={filter.state === WITHOUT_STATE}
							dimmed={Boolean(filter.state) && filter.state !== WITHOUT_STATE}
							title={t('Filter the list by {{label}}', { label: t('Without federal state') })}
							onToggle={() => onToggle('state', WITHOUT_STATE)}
						/>
					</ul>
				</div>
			</div>

			{chips.length > 0 && (
				<div className={styles.activeFilter}>
					<span className={styles.activeFilterLabel}>{t('Filtered by')}</span>
					{chips.map(chip => (
						<button
							key={chip.kind}
							type="button"
							className={styles.chip}
							title={t('Remove the filter {{label}}', { label: chip.label })}
							onClick={() => onToggle(chip.kind, chip.value)}
						>
							{chip.label}
							<span aria-hidden="true" className={styles.chipCross}>
								×
							</span>
						</button>
					))}
					<span className={styles.activeFilterCount}>
						{t('{{shown}} of {{all}} administrators', { shown: visibleCount, all: stats.total })}
					</span>
					<button type="button" className={styles.reset} onClick={onReset}>
						{t('Reset filters')}
					</button>
				</div>
			)}

			{stats.withoutLogin > 0 && (
				<p className={styles.note}>
					{t('Last sign-in has been recorded since 06.08.2026 — earlier sign-ins are unknown.')}{' '}
					{/* Nicht `count` als Name: i18next behandelt den Schlüssel
					    sonst als Mehrzahlform und findet die Übersetzung nicht. */}
					{t('Accounts without a sign-in since then: {{amount}}', { amount: stats.withoutLogin })}
				</p>
			)}
		</section>
	)
}

Overview.propTypes = {
	admins: PropTypes.array.isRequired,
	// Was gerade gilt: `{ role, state }`, beides einzeln `null`, wenn nicht
	// gefiltert wird.
	filter: PropTypes.object.isRequired,
	// (kind, value) — derselbe Wert noch einmal schaltet den Filter wieder ab.
	onToggle: PropTypes.func.isRequired,
	onReset: PropTypes.func.isRequired,
	visibleCount: PropTypes.number.isRequired
}
